import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ArrowRight, ArrowUpRight, Fuel, Factory, FlaskConical, Sprout, Atom, Zap, Award, FlaskRound, ShieldCheck, Thermometer, MapPin } from "lucide-react";
import { Reveal, SectionHead, Eyebrow, Counter, CTASection, usePageMeta } from "../components/ui-bits";
import { IMG, imgUrl, stats, capabilities, scaleMetrics, services, industries, clients, projects as staticProjects } from "../data/content";
import { useCmsList, useCmsSingleton } from "../hooks/useCmsContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const industryIcons = { Fuel, Factory, FlaskConical, Sprout, Atom, Zap };

const HERO_FALLBACK = {
  hero_badges: ["Est. 1966", "ASME 'U' & 'U2' Stamp Holder"],
  hero_title: "Engineering Excellence.\nHeavy Fabrication.\nProject Execution.",
  hero_sub: "For six decades, G. R. Engineering has designed, fabricated and erected critical pressure equipment — columns up to 110 m, vessels up to 425 MT — for India's leading refineries, petrochemical and energy complexes.",
  hero_img: "hero-panipat",
  hero_metrics: [["110 m", "Longest Columns"], ["425 MT", "Single-Piece Vessels"], ["2,230 MT", "Site-Fabricated Tower"], ["500 kg/cm²g", "Hydrotest Capability"]],
  cta_primary_text: "Explore Our Capabilities", cta_primary_link: "/capabilities",
  cta_secondary_text: "Contact Us", cta_secondary_link: "/contact",
};

const Hero = () => {
  const h = useCmsSingleton("home", HERO_FALLBACK);
  const lines = String(h.hero_title || "").split("\n").filter(Boolean);
  const heroRef = useRef(null);
  // micro-parallax: desktop pointer only (CSS neutralises it on touch/mobile/reduced-motion)
  const onHeroMove = (e) => {
    const el = heroRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--px", ((e.clientX - r.left) / r.width - 0.5).toFixed(3));
    el.style.setProperty("--py", ((e.clientY - r.top) / r.height - 0.5).toFixed(3));
  };
  return (
  <section ref={heroRef} onMouseMove={onHeroMove} className="relative bg-[#0A1128] overflow-hidden" data-testid="home-hero">
    <div className="absolute inset-0 hero-parallax">
      <img src={imgUrl(h.hero_img)} alt="Main fractionator column fabricated by G. R. Engineering for IOCL Panipat Refinery" className="w-full h-full object-cover img-industrial animate-kenburns" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A1128]/95 via-[#0A1128]/70 to-[#0A1128]/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128] via-transparent to-transparent" />
    </div>
    <div className="absolute inset-0 blueprint-grid opacity-70 hero-grid-drift" />
    <div className="hero-scan" aria-hidden="true" />
    {/* decorative HUD edge details — desktop only, purely visual */}
    <div className="hero-hud absolute top-24 right-8 w-40 h-1.5 hud-tick-rail hidden lg:block" aria-hidden="true" />
    <div className="hero-hud absolute bottom-28 left-8 w-40 h-1.5 hud-tick-rail hidden lg:block" aria-hidden="true" />
    <div className="hero-hud absolute top-28 left-10 hidden lg:block hud-plus" aria-hidden="true" />
    <div className="hero-hud absolute bottom-32 right-12 hidden lg:block hud-plus" aria-hidden="true" />
    <div className="hero-hud absolute top-32 right-10 hidden lg:block font-mono-tech text-[9px] tracking-[0.3em] text-white/35 uppercase select-none" aria-hidden="true">Mumbai · Tarapur</div>
    <div className="absolute left-0 top-0 bottom-0 w-px bg-white/10 hidden lg:block" />
    <div className="container-gre relative pt-24 pb-16 lg:pt-36 lg:pb-28 min-h-[82vh] flex flex-col justify-center">
      <Reveal>
        <div className="flex flex-wrap items-center gap-3" data-testid="hero-badges">
          {(h.hero_badges || []).map((b, i) => (
            <span key={b} className={`font-mono-tech text-[11px] font-semibold uppercase tracking-[0.25em] text-white px-4 py-2 ${i === 0 ? "border border-white/25 backdrop-blur-sm" : "bg-[#C5221F]"}`}>{b}</span>
          ))}
        </div>
      </Reveal>
      <h1 className="font-display mt-8 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white leading-[1.04] max-w-4xl uppercase" data-testid="hero-headline">
        {lines.map((l, i) => (
          <span key={i} className="hero-line-mask">
            <span className="hero-line" style={{ animationDelay: `${0.2 + i * 0.14}s` }}>
              {i === lines.length - 1 ? <span className="text-[#E8574F]">{l}</span> : l}
            </span>
          </span>
        ))}
      </h1>
      <Reveal delay={0.2}>
        <p className="mt-7 max-w-xl text-base lg:text-lg text-slate-300 leading-relaxed">
          {h.hero_sub}
        </p>
      </Reveal>
      <Reveal delay={0.3}>
        <div className="mt-10 flex flex-wrap gap-4">
          <Link to={h.cta_primary_link || "/capabilities"} data-testid="hero-explore-capabilities-btn" className="group inline-flex items-center gap-3 bg-[#C5221F] hover:bg-[#A81B18] text-white font-display font-semibold px-8 py-4 text-sm uppercase tracking-widest transition-colors duration-300">
            {h.cta_primary_text || "Explore Our Capabilities"}
            <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link to={h.cta_secondary_link || "/contact"} data-testid="hero-contact-btn" className="inline-flex items-center gap-3 border border-white/40 hover:border-white text-white hover:bg-white/5 font-display font-semibold px-8 py-4 text-sm uppercase tracking-widest transition-colors duration-300">
            {h.cta_secondary_text || "Contact Us"}
          </Link>
        </div>
      </Reveal>
    </div>
    <div className="relative border-t border-white/10 bg-[#0A1128]/80 backdrop-blur-md" data-testid="hero-metric-bar">
      <div className="container-gre grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/10">
        {(h.hero_metrics || []).map(([v, l]) => (
          <div key={l} className="px-5 py-6">
            <p className="font-display text-2xl lg:text-3xl font-extrabold text-white tracking-tight">{v}</p>
            <p className="mt-1 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-slate-400">{l}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
  );
};

const Stats = () => (
  <section className="bg-white border-b border-slate-200 blueprint-grid-light" data-testid="stats-section">
    <div className="container-gre py-16 lg:py-20 grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
      {stats.map((s) => (
        <div key={s.label} className="bg-white p-7 lg:p-9">
          <p className="font-display text-4xl lg:text-5xl font-black tracking-tight text-[#0A1128]">
            <Counter value={s.value} suffix={s.suffix || ""} plain={s.plain} />
          </p>
          <p className="mt-3 font-display text-sm font-bold text-slate-800 uppercase tracking-wide">{s.label}</p>
          <p className="mt-1 text-xs text-slate-500">{s.sub}</p>
        </div>
      ))}
    </div>
  </section>
);

const AboutPreview = () => (
  <section className="bg-white" data-testid="about-preview">
    <div className="container-gre py-20 lg:py-28 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
      <div className="relative">
        <Reveal>
          <img src={IMG("columns-crane")} alt="Column erection at refinery site" className="w-full aspect-[4/3] object-cover img-industrial border border-slate-200" />
        </Reveal>
        <Reveal delay={0.15}>
          <div className="absolute -bottom-8 -right-2 sm:right-8 bg-[#C5221F] text-white px-7 py-6 max-w-[240px] shadow-2xl">
            <p className="font-display text-3xl font-black">1966</p>
            <p className="mt-1 text-xs uppercase tracking-widest font-semibold">Incorporated — six decades of engineering</p>
          </div>
        </Reveal>
      </div>
      <div className="pt-8 lg:pt-0">
        <SectionHead
          eyebrow="About G. R. Engineering"
          title="An established engineering & fabrication organisation, built on its people"
          sub="Headquartered in Mumbai with manufacturing works at Tarapur, G. R. Engineering Private Limited is an ASME 'U' & 'U2' stamp holder delivering world-class products and services to the hydrocarbon and process industries."
        />
        <Reveal delay={0.2}>
          <ul className="mt-8 space-y-3.5">
            {["Quality workmanship on every weld", "Competitive costs & on-time delivery", "Continually upgraded processes and skills", "Highly skilled, experienced engineers & staff"].map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm text-slate-700">
                <span className="mt-1.5 w-2 h-2 bg-[#C5221F] shrink-0" /> {t}
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.28}>
          <Link to="/about" data-testid="about-preview-btn" className="group mt-9 inline-flex items-center gap-3 bg-[#0A1128] hover:bg-[#C5221F] text-white font-display font-semibold px-7 py-3.5 text-xs uppercase tracking-widest transition-colors duration-300">
            About G. R. Engineering
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const CapabilitiesGrid = () => {
  const cards = useCmsList("capability_card", capabilities);
  return (
  <section className="bg-slate-50 border-y border-slate-200" data-testid="capabilities-section">
    <div className="container-gre py-20 lg:py-28">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
        <SectionHead eyebrow="Core Capabilities" title="From single vessels to complete EPC packages" />
        <Reveal delay={0.2}>
          <Link to="/products" className="group inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-[#C5221F]" data-testid="view-all-products-link">
            View Products <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
      <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
        {cards.map((c, i) => (
          <Reveal key={c.title} delay={(i % 4) * 0.07}>
            <Link to={c.to} className="group block bg-white h-full card-lift border border-transparent tech-corner" data-testid={`capability-card-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="overflow-hidden aspect-[4/3]">
                <img src={IMG(c.img)} alt={c.title} loading="lazy" className="w-full h-full object-cover img-industrial transition-transform duration-700 group-hover:scale-[1.07]" />
              </div>
              <div className="p-6">
                <h3 className="font-display text-base font-bold text-slate-900 flex items-center justify-between gap-2">
                  {c.title}
                  <ArrowUpRight size={16} className="text-slate-300 group-hover:text-[#C5221F] transition-colors duration-300 shrink-0" />
                </h3>
                <p className="mt-2.5 text-[13px] leading-relaxed text-slate-600">{c.desc}</p>
              </div>
              <div className="h-0.5 w-0 bg-[#C5221F] transition-all duration-300 group-hover:w-full" />
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
  );
};

const EngineeringScale = () => {
  const rawGroups = useCmsList("scale_group", scaleMetrics);
  const groups = rawGroups.map((g) => ({
    group: g.group,
    items: (g.items || []).map((it) => (Array.isArray(it) ? { label: it[0], value: it[1] } : it)),
  }));
  return (
  <section className="relative bg-[#0A1128] overflow-hidden" data-testid="engineering-scale">
    <div className="absolute inset-0">
      <img src={IMG("columns-panorama")} alt="" className="w-full h-full object-cover img-industrial opacity-15" />
      <div className="absolute inset-0 blueprint-grid" />
    </div>
    <div className="container-gre relative py-20 lg:py-28">
      <SectionHead
        light
        eyebrow="Engineering Scale"
        title="Built for equipment others can't move"
        sub="Verified maximum parameters for shop-fabricated equipment — and site fabrication for anything larger."
      />
      <div className="mt-14 grid lg:grid-cols-2 gap-px bg-white/10 border border-white/10">
        {groups.map((g) => (
          <div key={g.group} className="bg-[#0A1128]/70 backdrop-blur-sm p-8 lg:p-10">
            <p className="font-mono-tech text-xs font-semibold uppercase tracking-[0.25em] text-red-400">{g.group}</p>
            <div className="mt-7 grid grid-cols-2 gap-x-8 gap-y-8">
              {g.items.map((it) => (
                <div key={it.label}>
                  <p className="font-display text-3xl lg:text-4xl font-black text-white tracking-tight">{it.value}</p>
                  <p className="mt-1.5 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-slate-400">{it.label}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <Reveal delay={0.15}>
        <p className="mt-8 text-sm text-slate-400 max-w-2xl">
          Beyond these limits, G. R. Engineering fabricates at site — including a 13 m diameter, 1,784 MT gasoline fractionator and a 2,230 MT propylene fractionator for HMEL Bathinda.
        </p>
      </Reveal>
    </div>
  </section>
  );
};

const ServicesPreview = () => (
  <section className="bg-white" data-testid="services-preview">
    <div className="container-gre py-20 lg:py-28">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
        <SectionHead eyebrow="Services" title="Full-lifecycle project services" sub="Complete project management for the construction of process plants — oil & gas refineries, petrochemicals, fertilizer and nuclear power — on EPC / LSTK basis." />
        <Reveal delay={0.2}>
          <Link to="/services" className="group inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-[#C5221F]" data-testid="view-all-services-link">
            All Services <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
        {services.slice(0, 9).map((s, i) => (
          <Reveal key={s.title} delay={(i % 3) * 0.07}>
            <div className="bg-white p-7 h-full group hover:bg-slate-50 transition-colors duration-300">
              <span className="font-mono-tech text-xs font-bold text-slate-300 group-hover:text-[#C5221F] transition-colors duration-300">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-3 font-display text-base font-bold text-slate-900">{s.title}</h3>
              <p className="mt-2 text-[13px] leading-relaxed text-slate-600">{s.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Industries = () => (
  <section className="bg-slate-50 border-y border-slate-200" data-testid="industries-section">
    <div className="container-gre py-20 lg:py-28">
      <SectionHead eyebrow="Industries Served" title="Trusted across the process industries" />
      <div className="mt-14 grid grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
        {industries.map((ind, i) => {
          const Icon = industryIcons[ind.icon];
          return (
            <Reveal key={ind.name} delay={(i % 3) * 0.07}>
              <div className="bg-white p-7 lg:p-9 h-full group card-lift border border-transparent" data-testid={`industry-${ind.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <Icon size={26} className="text-[#C5221F]" strokeWidth={1.75} />
                <h3 className="mt-5 font-display text-lg font-bold text-slate-900">{ind.name}</h3>
                <p className="mt-2 text-[13px] leading-relaxed text-slate-600">{ind.desc}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const FeaturedProjects = () => {
  const [list, setList] = useState(staticProjects);
  useEffect(() => {
    axios.get(`${API}/projects`).then((r) => {
      if (Array.isArray(r.data) && r.data.length) setList(r.data);
    }).catch(() => {});
  }, []);
  const featured = list.filter((p) => p.featured).slice(0, 4);
  return (
    <section className="bg-white" data-testid="featured-projects">
      <div className="container-gre py-20 lg:py-28">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <SectionHead eyebrow="Featured Projects" title="Equipment that defines India's mega-refineries" />
          <Reveal delay={0.2}>
            <Link to="/projects" className="group inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-[#C5221F]" data-testid="view-all-projects-link">
              All Projects <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-14 grid md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
          {featured.map((p, i) => (
            <Reveal key={p.id} delay={(i % 2) * 0.08}>
              <Link to={`/projects/${p.id}`} className="group relative block h-96 lg:h-[26rem] overflow-hidden tech-corner" data-testid={`featured-project-${p.id}`}>
                <img src={imgUrl(p.img)} alt={p.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover img-industrial transition-transform duration-1000 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/95 via-[#0A1128]/35 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-7">
                  <div className="h-0.5 w-8 bg-[#C5221F] mb-3 transition-all duration-500 group-hover:w-16" />
                  <p className="font-mono-tech text-[10px] font-semibold uppercase tracking-[0.25em] text-red-400">{p.client} · {p.location}</p>
                  <h3 className="mt-2.5 font-display text-lg lg:text-xl font-bold text-white leading-snug max-w-lg transition-transform duration-500 group-hover:-translate-y-0.5">{p.title}</h3>
                  <p className="mt-2 text-xs text-slate-300 line-clamp-2 max-w-lg">{p.scope}</p>
                </div>
                <div className="absolute top-5 right-5">
                  <span className={`font-mono-tech text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 ${p.status === "Ongoing" ? "bg-[#C5221F] text-white" : "bg-white/90 text-slate-800"}`}>{p.status}</span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const fmtPosted = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return ""; }
};

const CurrentOpenings = () => {
  const [jobs, setJobs] = useState([]);
  useEffect(() => {
    axios.get(`${API}/jobs-public`).then((r) => setJobs(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);
  if (!jobs.length) return null;
  const top = jobs.slice(0, 3);
  return (
    <section className="bg-white border-b border-slate-200" data-testid="current-openings">
      <div className="container-gre py-20 lg:py-24">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <SectionHead eyebrow="Careers" title="Current Openings" sub="Explore career opportunities with G. R. Engineering Private Limited." />
          <Reveal delay={0.2}>
            <Link to="/careers" className="group inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-[#C5221F]" data-testid="view-all-careers-link">
              View All Careers <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
          {top.map((j, i) => (
            <Reveal key={j.id} delay={i * 0.07}>
              <Link to={`/careers/${j.id}`} className="group block bg-white p-7 h-full card-lift border border-transparent" data-testid={`opening-card-${j.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-display text-base font-bold text-slate-900 group-hover:text-[#C5221F] transition-colors duration-200">{j.title}</h3>
                  <ArrowUpRight size={16} className="text-slate-300 group-hover:text-[#C5221F] transition-colors duration-300 shrink-0 mt-0.5" />
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-slate-500">
                  {j.location && <span className="flex items-center gap-1.5"><MapPin size={12} className="text-[#C5221F]" />{j.location}</span>}
                  {j.department && <span className="font-mono-tech text-[10px] font-semibold uppercase tracking-widest border border-slate-200 px-2 py-0.5">{j.department}</span>}
                  {j.experience && <span>{j.experience}</span>}
                </div>
                {j.description && <p className="mt-3.5 text-[13px] leading-relaxed text-slate-600 line-clamp-2">{j.description}</p>}
                <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4">
                  <span className="text-[11px] text-slate-400">{j.posted_at ? `Posted ${fmtPosted(j.posted_at)}` : ""}</span>
                  <span className="text-[11px] font-bold uppercase tracking-widest text-[#C5221F]">View &amp; Apply</span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const QualityStrip = () => (
  <section className="relative bg-[#0A1128] overflow-hidden" data-testid="quality-section">
    <div className="absolute inset-0">
      <img src={IMG("bpcl-night")} alt="" className="w-full h-full object-cover img-industrial opacity-20" />
      <div className="absolute inset-0 blueprint-grid opacity-60" />
    </div>
    <div className="container-gre relative py-20 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">
      <div>
        <SectionHead
          light
          eyebrow="Quality & Testing"
          title="Accredited engineering, verified at every stage"
          sub="ASME 'U' & 'U2' stamp holder with in-house NDT, an in-house NABL-accredited mechanical testing laboratory and heat treatment infrastructure — inspection and testing are part of the process, not an afterthought."
        />
        <Reveal delay={0.2}>
          <Link to="/quality" data-testid="quality-explore-btn" className="group mt-9 inline-flex items-center gap-3 border border-white/40 hover:border-white text-white font-display font-semibold px-7 py-3.5 text-xs uppercase tracking-widest transition-colors duration-300">
            Quality & Testing <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
      <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10">
        {[
          [Award, "ASME 'U' & 'U2'", "Stamp holder — pressure vessels"],
          [FlaskRound, "In-House NDT", "RT, UT, TOFD, PAUT, MT, PT, PMI"],
          [ShieldCheck, "NABL Laboratory", "In-house mechanical testing"],
          [Thermometer, "Heat Treatment", "Bogie hearth furnace 6×6×12 m"],
        ].map(([Icon, t, s]) => (
          <div key={t} className="bg-[#0A1128]/70 backdrop-blur-sm p-6 lg:p-8">
            <Icon size={24} className="text-[#E8574F]" strokeWidth={1.75} />
            <p className="mt-4 font-display text-sm font-bold text-white">{t}</p>
            <p className="mt-1.5 text-xs text-slate-400 leading-relaxed">{s}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const MajorClients = () => {
  const [clients, setClients] = useState([]);
  useEffect(() => {
    axios.get(`${API}/clients`).then((r) => setClients(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);
  if (!clients.length) return null;
  return (
    <section className="bg-slate-50 border-b border-slate-200" data-testid="major-clients">
      <div className="container-gre py-16 lg:py-20">
        <SectionHead eyebrow="Trusted By" title="Major Clients" />
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-slate-200 border border-slate-200">
          {clients.map((c, i) => (
            <Reveal key={c.id} delay={(i % 5) * 0.05}>
              <div className="bg-white p-6 h-full flex flex-col items-center justify-center text-center" data-testid={`client-logo-${c.id}`}>
                {c.logo_url ? (
                  <img src={c.logo_url} alt={c.name} loading="lazy" className="h-14 w-auto object-contain" />
                ) : (
                  <span className="font-display text-base font-bold text-slate-700">{c.name}</span>
                )}
                <p className="mt-3 text-[11px] font-semibold uppercase tracking-widest text-slate-500">{c.name}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const ClientsTicker = () => (
  <section className="bg-white border-b border-slate-200 overflow-hidden" data-testid="clients-section">
    <div className="container-gre pt-14 pb-6">
      <p className="font-mono-tech text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-400 text-center">Trusted by India's leading energy & process companies</p>
    </div>
    <div className="relative pb-14">
      <div className="flex whitespace-nowrap animate-ticker w-max">
        {[...clients, ...clients].map((c, i) => (
          <span key={i} className="mx-8 font-display text-xl lg:text-2xl font-bold text-slate-300 select-none">{c}</span>
        ))}
      </div>
    </div>
  </section>
);

const Home = () => {
  usePageMeta(
    "G. R. Engineering Private Limited — Heavy Fabrication, Pressure Vessels & EPC | grengg.com",
    "Est. 1966. ASME 'U' & 'U2' stamp holder. Pressure vessels, columns & towers, heat exchangers, reactors, mounded storage and Horton spheres. EPC / LSTK for refinery, petrochemical and power industries."
  );
  const home = useCmsSingleton("home", {});
  const show = (k) => home[k] !== false;
  return (
    <>
      <Hero />
      {show("show_stats") && <Stats />}
      {show("show_about") && <AboutPreview />}
      {show("show_capabilities") && <CapabilitiesGrid />}
      {show("show_scale") && <EngineeringScale />}
      {show("show_services") && <ServicesPreview />}
      {show("show_industries") && <Industries />}
      {show("show_projects") && <FeaturedProjects />}
      {show("show_careers") && <CurrentOpenings />}
      {show("show_quality") && <QualityStrip />}
      {show("show_clients") && <><ClientsTicker /><MajorClients /></>}
      <CTASection />
    </>
  );
};

export default Home;
